import { useState, useEffect, useCallback, useRef } from 'react';
import {
  isInfluencerProfileComplete,
  isBrandProfileComplete,
  getMissingInfluencerFields,
  getMissingBrandFields,
  getProfileCompletionPercentage,
} from '../../utils/shared/profileCompletion';
import { getToken, isTokenValid } from '../../utils/auth/tokenManager';
import { get as apiGet } from '../../redux/api';

/**
 * Hook to check if user profile is complete
 * @param {string} userType - 'influencer' or 'brand'
 * @param {boolean} skip - Skip the check (e.g., for onboarding pages)
 * @param {Object} options - Additional options
 * @param {boolean} options.failSecure - If true, block access on errors (default: true)
 * @param {number} options.retryCount - Number of retry attempts (default: 2)
 */
function useProfileCompletion(userType, skip = false, options = {}) {
  const { failSecure = true, retryCount = 2 } = options;

  // Fail-secure: default to false (incomplete) until verified
  const [isComplete, setIsComplete] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [missingFields, setMissingFields] = useState([]);
  const [profile, setProfile] = useState(null);
  const [error, setError] = useState(null);
  const [completionPercentage, setCompletionPercentage] = useState(0);
  const [retryAttempt, setRetryAttempt] = useState(0);

  // Track if component is mounted to prevent state updates after unmount
  const isMounted = useRef(true);

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  const checkProfileCompletion = useCallback(async (attempt = 0) => {
    // Skip check if requested
    if (skip || !userType) {
      if (isMounted.current) {
        setIsComplete(true);
        setIsLoading(false);
        setError(null);
      }
      return;
    }

    let isRetrying = false;

    try {
      if (isMounted.current) {
        setIsLoading(true);
        setError(null);
      }

      // Use tokenManager to get and validate token
      const token = getToken();
      if (!token || !isTokenValid(token)) {
        // No valid token - let ProtectedRoute handle auth
        // Fail-secure: don't set as complete, let the route guard handle it
        if (isMounted.current) {
          setIsComplete(false);
          setIsLoading(false);
        }
        return;
      }

      const endpoint = userType === 'influencer'
        ? '/api/influencer/profile'
        : '/api/brand/profile';

      // Use the axios instance for consistent interceptor handling
      const response = await apiGet(endpoint);
      const profileData = response.data;

      if (!isMounted.current) return;

      setProfile(profileData);

      let complete = false;
      let missing = [];

      if (userType === 'influencer') {
        complete = isInfluencerProfileComplete(profileData);
        missing = getMissingInfluencerFields(profileData);
      } else if (userType === 'brand') {
        complete = isBrandProfileComplete(profileData);
        missing = getMissingBrandFields(profileData);
      }

      setIsComplete(complete);
      setMissingFields(missing);
      setCompletionPercentage(getProfileCompletionPercentage(userType, profileData));
      setRetryAttempt(0); // Reset retry count on success
    } catch (err) {
      if (!isMounted.current) return;

      console.error('Error checking profile completion:', err);

      if (err.response?.status === 404) {
        setIsComplete(false);
        setProfile(null);
        setMissingFields([]);
        setCompletionPercentage(0);
        setError(null); // Not an error, just incomplete
        if (isMounted.current) {
          setIsLoading(false);
        }
        return;
      }

      if (err.response?.status === 401 || err.response?.status === 403) {
        setIsComplete(false);
        setError('Authentication error');
        if (isMounted.current) {
          setIsLoading(false);
        }
        return;
      }

      // Retry logic for network/server errors
      if (attempt < retryCount) {
        isRetrying = true;
        setRetryAttempt(attempt + 1);
        const retryDelay = 1000 * (attempt + 1); // Exponential backoff
        setTimeout(() => {
          if (isMounted.current) {
            checkProfileCompletion(attempt + 1);
          }
        }, retryDelay);
        // Keep isLoading true during retries - don't set it to false here
        return;
      }

      // Max retries reached - apply fail-secure behavior
      const errorMessage = err.response?.data?.message || err.message || 'Failed to verify profile';
      setError(errorMessage);

      if (failSecure) {
        // Fail-secure: treat as incomplete (blocks access)
        setIsComplete(false);
        setMissingFields([]);
        setCompletionPercentage(0);
      } else {
        // Fail-open: allow access (original behavior for backwards compatibility)
        setIsComplete(true);
      }

      if (isMounted.current) {
        setIsLoading(false);
      }
    } finally {
      // Only set loading to false if we're not retrying
      // (success case and max retries case are handled explicitly above)
      if (isMounted.current && !isRetrying) {
        // Success case: set loading to false
        // (error cases that don't retry are handled explicitly in catch block)
        setIsLoading(false);
      }
    }
  }, [userType, skip, failSecure, retryCount]);

  useEffect(() => {
    checkProfileCompletion(0);
  }, [checkProfileCompletion]);

  // Function to manually refresh the check
  const refresh = useCallback(() => {
    checkProfileCompletion(0);
  }, [checkProfileCompletion]);

  return {
    isComplete,
    isLoading,
    missingFields,
    profile,
    error,
    completionPercentage,
    refresh,
    retryAttempt,
  };
}

export default useProfileCompletion;
